import { ChevronDown, Download, FileSpreadsheet } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTRPC } from '../trpc.js';
import { MenuSurface } from '../ui/Menu.js';
import { type SheetScope, useSheetExport } from './useSheetExport.js';

export interface ExportButtonProps {
  /** What the sheet holds; built on click, so it is the view as it is then. */
  scope: () => SheetScope | Promise<SheetScope>;
  downloadCsv: () => void;
  disabled?: boolean;
}

/**
 * "Export to Google Sheets", with the CSV download behind the caret. A server
 * without the Google setup offers the download alone.
 */
export function ExportButton(props: ExportButtonProps): React.JSX.Element {
  const trpc = useTRPC();
  const status = useQuery(trpc.exports.driveStatus.queryOptions());
  const sheet = useSheetExport();
  const [caret, setCaret] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  if (status.data?.configured !== true) {
    return (
      <button
        className="button primary"
        type="button"
        disabled={props.disabled}
        onClick={props.downloadCsv}
      >
        <Download aria-hidden="true" />
        Download CSV
      </button>
    );
  }

  return (
    <span className="split-button">
      <button
        className="button primary"
        type="button"
        disabled={props.disabled === true || sheet.pending}
        onClick={() => sheet.exportToSheet(props.scope())}
      >
        <FileSpreadsheet aria-hidden="true" />
        {sheet.pending ? 'Creating the sheet…' : 'Export to Google Sheets'}
      </button>
      <button
        ref={setCaret}
        className="button primary split-caret"
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More export options"
        disabled={props.disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <ChevronDown aria-hidden="true" />
      </button>
      {open && caret !== null && (
        <MenuSurface
          anchor={{ kind: 'element', element: caret }}
          label="More export options"
          items={[{ label: 'Download CSV', onSelect: props.downloadCsv }]}
          onClose={(reason) => {
            setOpen(false);
            if (reason !== 'outside') caret.focus();
          }}
        />
      )}
    </span>
  );
}
